<template>
  <div class="row">
    <div class="col-12">
      <div class="row mb-3">
        <div class="col-12">
          <h3>My Licenses</h3>
          <p v-if="companyStore.userHasCompany">
            Licenses that can be used exclusively by you. You can grant access
            to others by moving them to your company pool.
          </p>
        </div>
      </div>
      <div class="row">
        <div
          class="col-12 offset-md-6 col-md-6 d-flex flex-column flex-md-row gap-2 justify-content-md-end"
        >
          <StoreButton
            class="mt-md-0 w-md-auto"
            type="button"
            @click="
              withCompleteRegistration(
                () => (showImportLicenseOrderModal = true),
              )
            "
          >
            Import Order
          </StoreButton>
          <StoreButton
            class="mt-md-0 w-md-auto"
            type="button"
            @click="
              withCompleteRegistration(() => (showAddLicenseModal = true))
            "
          >
            Import License
          </StoreButton>
          <RefreshButton :on-refresh="licensesStore.createRefreshTask" />
        </div>
      </div>
      <div class="row">
        <div
          class="col-12 mt-md-0 mt-2 col-md-6 d-flex justify-content-center justify-content-md-start"
        >
          <SwitchField
            id="multiInstallMode"
            v-model="multiInstallMode"
            class="d-inline-flex align-items-center gap-2"
          >
            <span class="align-middle">Multi-License Install Mode</span>
          </SwitchField>
        </div>
      </div>

      <MyLicensesSingleMode
        v-if="!multiInstallMode"
        :handle-install="handleInstall"
      />
      <MyLicensesMultiMode
        v-else-if="multiInstallMode"
        :handle-install="handleInstall"
      />

      <KnowledgeBaseComponent filter-string="PLCnext_Store_license" />
    </div>
    <ImportLicenseFromTicketIdModal
      v-model="showAddLicenseModal"
      :callback-function="importTicketId"
    />
    <ImportLicenseOrderModal
      v-model="showImportLicenseOrderModal"
      :callback-function="importTicketIds"
    />
  </div>
</template>

<script setup lang="ts">
import KnowledgeBaseComponent from "~/components/shared/KnowledgeBaseComponent.vue";
import { useLicensesStore } from "~/stores/licensesstore";
import userIsLoggedIn from "~/middleware/userIsLoggedIn";
import RefreshButton from "~/components/shared/buttons/RefreshButton.vue";
import MyLicensesSingleMode from "~/components/specific/licenses/MyLicensesSingleMode.vue";
import ImportLicenseFromTicketIdModal from "~/components/specific/licenses/ImportLicenseFromTicketIdModal.vue";
import type { ImportTicketIdForm } from "~/validations/import-ticketid";
import { useToastStore } from "~/stores/ui/toaststore";
import { withCompleteRegistration } from "~/helpers/withCompleteRegistration";
import MyLicensesMultiMode from "~/components/specific/licenses/MyLicensesMultiMode.vue";
import type {
  WebModelGetAppSettings,
  WebModelGetLicense,
} from "~/model/api/pLCnextStoreFrontendAPI.schemas";
import StoreButton from "~/components/shared/buttons/StoreButton.vue";
import { useCompanyStore } from "~/stores/companystore";
import SwitchField from "~/components/shared/inputfields/SwitchField.vue";
import ImportLicenseOrderModal from "~/components/specific/licenses/ImportLicenseOrderModal.vue";
import type { LicenseOrderXmlForm } from "~/validations/license-order-xml";

const licensesStore = useLicensesStore();
const companyStore = useCompanyStore();

definePageMeta({
  middleware: [userIsLoggedIn],
  layout: "licenses",
});

const multiInstallMode = ref(false);

onMounted(async () => {
  await licensesStore.loadLicensesForUser();
});

const showAddLicenseModal = ref(false);
const showImportLicenseOrderModal = ref(false);

const importTicketId = async (importTicketIdForm: ImportTicketIdForm) => {
  console.debug(
    "Importing license from ticket ID: " + importTicketIdForm.ticketId,
  );
  const success = await licensesStore.importLicenseFromTicketId(
    importTicketIdForm.ticketId,
  );
  if (success) {
    await licensesStore.loadLicensesForUser();
    showAddLicenseModal.value = false;
    useToastStore().addToastWithMessageAndTitle(
      "Success",
      "Added license",
      "success",
    );
  }
};

const importTicketIds = async (form: LicenseOrderXmlForm) => {
  console.debug("Import xml file for order licenses " + form);
  if (!form.xmlFile) {
    useToastStore().addToastWithMessageAndTitle(
      "No file selected",
      "Please select a file",
      "warning",
    );
    return false;
  }
  const data = await licensesStore.importLicenseOrderXmlFile(
    form.xmlFile,
    form.importForCompany,
  );
  if (data) {
    await licensesStore.loadLicensesForUser();
    useToastStore().addToastWithMessageAndTitle(
      "Success",
      "Added licenses",
      "success",
    );
  }
  return data; // we want to show imported/not imported licenses inside the modal
};

const router = useRouter();

const handleInstall = (
  appSettings: WebModelGetAppSettings | undefined,
  selectedLicenses: WebModelGetLicense[],
  selectedAppVersionId: number | undefined,
) => {
  if (appSettings && appSettings.externalInstallLink) {
    window.open(appSettings.externalInstallLink, "_blank");
    return;
  }
  let licensesToInstallQueryParamString = "";
  let licenseOptionsIdsQueryParamString = "";
  if (selectedLicenses && selectedLicenses.length > 0) {
    for (let i = selectedLicenses.length - 1; i >= 0; i--) {
      const selectedLicense = selectedLicenses[i];
      licensesToInstallQueryParamString += `&licenseIds=${selectedLicense?.id}`;
      licenseOptionsIdsQueryParamString += `&licenseOptionsToInstall=${selectedLicense?.licenseOption?.id}`;
    }
  }
  if (selectedAppVersionId) {
    router.push(
      `/install?appVersionId=${selectedAppVersionId}&installApp=true${licensesToInstallQueryParamString}${licenseOptionsIdsQueryParamString}`,
    );
  } else {
    // only selected license options
    router.push(
      `/install?${licensesToInstallQueryParamString}${licenseOptionsIdsQueryParamString}`,
    );
  }
};

useSeoMeta({ title: "PLCnext Store | My Licenses" });
</script>

<style scoped lang="scss"></style>
